/**
 * Product Service - API calls for product operations
 */

import {httpClient, HttpError} from '@/lib/http'
import type {ProductSubmissionFormData, CategoryDto} from '@/lib/validations/product-submission'

// ============================================================================
// TYPES
// ============================================================================

// User summary from API
export interface LinkedProductSummary {
    id: number
    slug: string
    name: string
    logoUrl: string
}

export interface UserSummary {
    id: number
    name: string
    username: string | null
    avatarUrl: string | null
    linkedProduct?: LinkedProductSummary | null
}

// ============================================================================
// MEDIA ITEM TYPES (New API)
// ============================================================================

// Request types for creating products
// User-chosen embed layout: zoom of the video inside its fixed frame
// (% of frame, 100–200) and pan offsets (% of frame, -50–50).
// Backend stores and echoes these verbatim.
export interface MediaEmbedLayout {
    scalePercent?: number
    offsetX?: number
    offsetY?: number
}

export type MediaItemRequest =
    | { type: 'image'; imageKey: string }
    | ({ type: 'video'; url: string } & MediaEmbedLayout)
    | ({ type: 'iframe'; src: string; aspectRatio: number } & MediaEmbedLayout)
    | { type: 'text'; content: string }
    | { type: 'gallery'; imageKeys: string[] }

// Response types from API
export type MediaItemResponse =
    | { type: 'image'; imageKey: string; imageUrl: string; thumbnailUrl: string | null }
    | ({ type: 'video'; url: string } & MediaEmbedLayout)
    | ({ type: 'iframe'; src: string; aspectRatio: number } & MediaEmbedLayout)
    | { type: 'text'; content: string }
    | { type: 'gallery'; images: Array<{ imageUrl: string; thumbnailUrl: string | null }> }

// Category simple DTO
export interface CategorySimpleDto {
    id: number
    name: string
    slug: string
}

export interface PreviewImageDto {
    imageKey: string
    imageUrl: string
    thumbnailUrl: string
}

// My product item - full details for owner view
export interface MyProductItem {
    id: number
    slug: string
    name: string
    tagline: string
    description: string
    logoUrl: string
    websiteUrl: string
    media: MediaItemResponse[] | null
    highlights: string[] | null
    categories: CategorySimpleDto[]
    statusTags: string[] | null
    launchWeekId: number
    launchDate: string | null
    voteCount: number
    viewCount: number
    commentCount: number
    approved: boolean
    approvedAt: string | null
    rejectedAt: string | null
    rejectionReason: string | null
    hiddenAt: string | null
    hiddenReason: string | null
    createdAt: string
    updatedAt: string
}

// My products API response
interface MyProductsResponse {
    products: MyProductItem[]
}

// Product summary from featured endpoint
interface ProductSummaryApi {
    id: number
    slug: string
    name: string
    description: string
    tagline: string
    logoUrl: string
    websiteUrl: string
    previewImages: PreviewImageDto[]
    categories: CategorySimpleDto[]
    statusTags: string[] | null
    launchDate: string | null
    voteCount: number
    viewCount: number
    commentCount: number
    isVoted: boolean
    createdBy: UserSummary
    createdAt: string
}

// Featured product item from API
interface FeaturedProductItemApi {
    position: number
    product: ProductSummaryApi
}

// Featured products response from API
interface FeaturedProductsResponseApi {
    products: FeaturedProductItemApi[]
}

// Product list response from API
interface ProductListResponse {
    products: ProductSummaryApi[]
}

// Query params for listing products
export interface ListProductsParams {
    weekStart?: string    // YYYY-MM-DD
    categoryId?: number
    sort?: 'popular' | 'new' | 'voted'
}

// ============================================================================
// GROUPED PRODUCTS BY WEEK TYPES
// ============================================================================

// Week response from API
export interface ProductWeekResponse {
    id: number
    weekStartDate: string  // ISO date (YYYY-MM-DD)
    weekEndDate: string    // ISO date (YYYY-MM-DD)
    name: string | null
    description: string | null
    createdAt: string
    updatedAt: string
}

// Statistics for a week
export interface WeekStats {
    totalProducts: number
    totalVotes: number
}

// Week with its products
export interface WeekWithProducts {
    week: ProductWeekResponse
    products: ProductSummaryApi[]
    stats: WeekStats
}

// Main grouped response
export interface GroupedProductListResponse {
    weeks: WeekWithProducts[]
    totalProducts: number
    weeksIncluded: number
}

// Query params for grouped products
export interface ListGroupedProductsParams {
    beforeWeekId?: number  // For pagination - fetch weeks before this ID
    limit?: number         // Number of weeks to fetch
    categoryId?: number
    sort?: 'popular' | 'new' | 'voted'
    weekStart?: string     // ISO date (YYYY-MM-DD) to filter by specific week
}

// Team member in product response
export interface ProductTeamMember {
    userId: number
    name: string
    username: string | null
    avatarUrl: string | null
    role: string
    joinedAt: string
}

// Team data in product response
export interface ProductTeam {
    owner: UserSummary
    members: ProductTeamMember[]
}

// Full product response from API
export interface ProductResponseApi {
    id: number
    slug: string
    name: string
    tagline: string
    description: string
    logoUrl: string
    websiteUrl: string
    media: MediaItemResponse[] | null
    previewImages: PreviewImageDto[]
    highlights: string[] | null
    categories: CategorySimpleDto[]
    statusTags: string[] | null
    launchWeekId: number
    launchDate: string | null
    voteCount: number
    viewCount: number
    commentCount: number
    isVoted: boolean
    approved: boolean
    approvedAt: string | null
    createdBy: UserSummary
    team?: ProductTeam
    createdAt: string
    updatedAt: string
}

// Result type for getProductBySlug with 403 handling
export type GetProductResult =
    | { status: 'success'; data: ProductResponseApi }
    | { status: 'forbidden'; message: string }
    | { status: 'not_found' }
    | { status: 'error'; message: string }

// FeaturedProduct type for the carousel (matches existing component type)
export interface FeaturedProduct {
    id: string
    slug: string
    name: string
    tagline: string
    description: string
    logoUrl: string
    featuredImageUrl: string
    thumbnailImages: string[]
    previewImages: PreviewImageDto[]
    categories: string[]
    statusTags?: ('investment' | 'feedback' | 'partnerships' | 'mentors' | 'hiring')[]
    voteCount: number
    viewCount: number
    commentCount: number
    founder: {
        id: string
        name: string
        username: string
    }
    isVoted: boolean
    weekRank: number
}

export interface ApiError {
    message: string
    code?: string
    details?: Record<string, string[]>
}

export interface VoteResponse {
    success: boolean
    voteCount: number
    isVoted: boolean
}

export interface VoteError {
    message: string
    code?: string
}

export interface ProductSubmissionResponse {
    success: boolean
    data?: {
        id: string
        name: string
        slug?: string
    }
    error?: ApiError
}

/**
 * Backend CreateProductRequest DTO (New API format)
 */
interface CreateProductRequest {
    slug: string
    name: string
    tagline: string
    description: string
    websiteUrl: string
    logoUrl: string
    media?: MediaItemRequest[]
    previewImages?: string[]
    highlights?: string[]
    categoryIds: number[]
    statusTags?: string[]
    launchDate?: string
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Build CreateProductRequest from form data
 * Supports both old format (with screenshots) and new format (with media)
 */
function buildProductRequest(data: ProductSubmissionFormData): CreateProductRequest {
    // Build request object using uploaded image keys
    const request: CreateProductRequest = {
        slug: data.slug,
        name: data.name,
        tagline: data.tagline,
        description: data.description || '',
        websiteUrl: data.websiteUrl || '',
        logoUrl: data.logo.key,
        categoryIds: data.categoryIds,
    }

    // Add media if provided (new format)
    if (data.media && data.media.length > 0) {
        request.media = data.media
    }

    // Add preview images if any
    if (data.previewImages && data.previewImages.length > 0) {
        request.previewImages = data.previewImages
    }

    // Add highlights if any
    if (data.highlights && data.highlights.length > 0) {
        request.highlights = data.highlights
    }

    // Add status tags if any
    if (data.statusTags && data.statusTags.length > 0) {
        request.statusTags = data.statusTags
    }

    // Add launch date if provided
    if (data.launchDate) {
        request.launchDate = data.launchDate
    }

    return request
}

/**
 * Handle API errors and convert to user-friendly response
 */
function handleError(error: unknown): ProductSubmissionResponse {
    if (error instanceof HttpError) {
        return {
            success: false,
            error: {
                message: error.message,
                code: error.code,
                details: error.details,
            },
        }
    }

    if (error instanceof Error) {
        return {
            success: false,
            error: {
                message: error.message,
                code: 'UNKNOWN_ERROR',
            },
        }
    }

    return {
        success: false,
        error: {
            message: 'Произошла неизвестная ошибка. Пожалуйста, попробуйте снова.',
            code: 'UNKNOWN_ERROR',
        },
    }
}

// ============================================================================
// API FUNCTIONS
// ============================================================================

/**
 * Submit a new product to the backend
 * Images should already be uploaded - this just sends the JSON payload
 */
export async function submitProduct(data: ProductSubmissionFormData): Promise<ProductSubmissionResponse> {
    try {
        // Build JSON payload from form data (images already uploaded)
        const requestBody = buildProductRequest(data)

        const responseData = await httpClient.post<{
            id?: string
            name?: string
            slug?: string
            data?: { id: string; name: string; slug?: string }
        }>('/products', requestBody as unknown as Record<string, unknown>)

        return {
            success: true,
            data: {
                id: responseData.id || responseData.data?.id || '',
                name: responseData.name || responseData.data?.name || '',
                slug: responseData.slug || responseData.data?.slug,
            },
        }
    } catch (error) {
        console.error('Product submission error:', error)
        return handleError(error)
    }
}

/**
 * Update an existing product (owner only)
 * PUT /api/v1/products/{slug} — full-replace semantics.
 *
 * Slug and name are immutable and ignored by the backend, so they are stripped
 * from the request. Every other editable field is sent as-is: omitting a
 * nullable field (media, previewImages, highlights, statusTags) clears it, so
 * callers must pass the product's complete current state with edits applied.
 */
export async function updateProduct(
    slug: string,
    data: ProductSubmissionFormData
): Promise<ProductSubmissionResponse> {
    try {
        // Same body shape as create, minus the immutable slug/name.
        const updateBody: Record<string, unknown> = { ...buildProductRequest(data) }
        delete updateBody.slug
        delete updateBody.name

        const responseData = await httpClient.put<ProductResponseApi>(
            `/products/${slug}`,
            updateBody
        )

        return {
            success: true,
            data: {
                id: String(responseData.id),
                name: responseData.name,
                slug: responseData.slug,
            },
        }
    } catch (error) {
        console.error('Product update error:', error)
        return handleError(error)
    }
}

/**
 * Save product as draft (backend endpoint)
 */
export async function saveDraftToBackend(
    data: Partial<ProductSubmissionFormData>
): Promise<ProductSubmissionResponse> {
    try {
        const responseData = await httpClient.post<{
            id?: string
            name?: string
            data?: { id: string; name: string }
        }>('/products/drafts', data as Record<string, unknown>)

        return {
            success: true,
            data: {
                id: responseData.id || responseData.data?.id || '',
                name: responseData.name || responseData.data?.name || '',
            },
        }
    } catch (error) {
        console.error('Draft save error:', error)
        return handleError(error)
    }
}

/**
 * Get user's draft products from backend
 */
export async function getUserDrafts(): Promise<{
    success: boolean
    data?: Partial<ProductSubmissionFormData>[]
    error?: ApiError
}> {
    try {
        const responseData = await httpClient.get<
            Partial<ProductSubmissionFormData>[] | { data: Partial<ProductSubmissionFormData>[] }
        >('/products/drafts')

        return {
            success: true,
            data: Array.isArray(responseData) ? responseData : responseData.data,
        }
    } catch (error) {
        console.error('Drafts fetch error:', error)

        if (error instanceof HttpError) {
            return {
                success: false,
                error: {
                    message: error.message,
                    code: error.code,
                },
            }
        }

        return {
            success: false,
            error: {
                message: 'Не удалось загрузить черновики',
                code: 'UNKNOWN_ERROR',
            },
        }
    }
}

/**
 * Fetch all categories from the backend
 */
export async function getCategories(): Promise<{
    success: boolean
    data?: CategoryDto[]
    error?: ApiError
}> {
    try {
        const responseData = await httpClient.get<{ data: CategoryDto[] }>('/categories')

        return {
            success: true,
            data: responseData.data,
        }
    } catch (error) {
        console.error('Categories fetch error:', error)

        if (error instanceof HttpError) {
            return {
                success: false,
                error: {
                    message: error.message,
                    code: error.code,
                },
            }
        }

        return {
            success: false,
            error: {
                message: 'Не удалось загрузить категории',
                code: 'UNKNOWN_ERROR',
            },
        }
    }
}

// ============================================================================
// FEATURED PRODUCTS (Public)
// ============================================================================

/**
 * Get a single product by slug (public)
 * @deprecated Use getProductBySlugSafe for proper error handling
 */
export async function getProductBySlug(slug: string): Promise<ProductResponseApi> {
    return httpClient.get<ProductResponseApi>(`/products/${slug}`)
}

/**
 * Get a single product by slug with proper error handling
 * Handles 403 (forbidden/moderation), 404 (not found), and other errors
 */
export async function getProductBySlugSafe(slug: string): Promise<GetProductResult> {
    try {
        const data = await httpClient.get<ProductResponseApi>(`/products/${slug}`)
        return {status: 'success', data}
    } catch (error) {
        if (error instanceof HttpError) {
            if (error.status === 403) {
                return {status: 'forbidden', message: 'Продукт находится на модерации'}
            }
            if (error.status === 404) {
                return {status: 'not_found'}
            }
            return {status: 'error', message: error.message}
        }
        return {status: 'error', message: 'Произошла неизвестная ошибка'}
    }
}

/**
 * Extract image URLs from media array (new API format)
 */
function extractImageUrlsFromMedia(media: MediaItemResponse[] | null): string[] {
    if (!media || media.length === 0) return []

    return media.flatMap((item) => {
        if (item.type === 'image') return [item.imageUrl]
        if (item.type === 'gallery') return item.images.map((img) => img.imageUrl)
        return []
    })
}

/**
 * Map status tags from API format to carousel format
 */
function mapStatusTags(
    tags: string[] | null
): ('investment' | 'feedback' | 'partnerships' | 'mentors' | 'hiring')[] | undefined {
    if (!tags || tags.length === 0) return undefined

    const validTags = ['investment', 'feedback', 'partnerships', 'mentors', 'hiring']
    const mapped = tags
        .map((tag) => {
            // Handle both formats: "seeking_investment" -> "investment", or already "investment"
            const normalized = tag.replace('seeking_', '').replace('want_', '').replace('looking_for_', '').replace('open_to_', '')
            return normalized
        })
        .filter((tag): tag is 'investment' | 'feedback' | 'partnerships' | 'mentors' | 'hiring' =>
            validTags.includes(tag)
        )

    return mapped.length > 0 ? mapped : undefined
}

/**
 * Get featured products with full details for the carousel
 * Fetches the featured list, then enriches with full product details
 */
export async function getFeaturedProductsForCarousel(): Promise<{
    success: boolean
    data?: FeaturedProduct[]
    error?: ApiError
}> {
    try {
        // Step 1: Get featured products list (public endpoint)
        const featuredResponse = await httpClient.get<FeaturedProductsResponseApi>('/products/featured')

        if (!featuredResponse.products || featuredResponse.products.length === 0) {
            return {
                success: true,
                data: [],
            }
        }

        // Sort by position
        const sortedProducts = [...featuredResponse.products].sort((a, b) => a.position - b.position)

        // Step 2: Fetch full details for each product in parallel
        const productDetailsPromises = sortedProducts.map((item) =>
            getProductBySlug(item.product.slug).catch((err) => {
                console.error(`Failed to fetch product ${item.product.slug}:`, err)
                return null
            })
        )

        const productDetails = await Promise.all(productDetailsPromises)

        // Step 3: Map to FeaturedProduct format
        const featuredProducts: FeaturedProduct[] = sortedProducts
            .map((item, index) => {
                const fullProduct = productDetails[index]

                // Use full product data if available, otherwise use summary
                if (fullProduct) {
                    // Extract image URLs from media array (new API format)
                    const imageUrls = extractImageUrlsFromMedia(fullProduct.media)

                    return {
                        id: String(fullProduct.id),
                        slug: fullProduct.slug,
                        name: fullProduct.name,
                        tagline: fullProduct.tagline,
                        description: fullProduct.description,
                        logoUrl: fullProduct.logoUrl,
                        // Use first image as featured image, fallback to logo
                        featuredImageUrl: imageUrls[0] || fullProduct.logoUrl,
                        // Use all images as thumbnails
                        thumbnailImages: imageUrls.slice(0, 4),
                        previewImages: fullProduct.previewImages,
                        categories: fullProduct.categories.map(cat => cat.name),
                        statusTags: mapStatusTags(fullProduct.statusTags),
                        voteCount: fullProduct.voteCount,
                        viewCount: fullProduct.viewCount,
                        commentCount: fullProduct.commentCount,
                        founder: {
                            id: String(fullProduct.createdBy.id),
                            name: fullProduct.createdBy.name,
                            username: fullProduct.createdBy.username || '',
                        },
                        isVoted: fullProduct.isVoted,
                        weekRank: item.position,
                    }
                }

                // Fallback to summary data if full fetch failed
                return {
                    id: String(item.product.id),
                    slug: item.product.slug,
                    name: item.product.name,
                    tagline: item.product.tagline,
                    description: item.product.tagline, // Use tagline as fallback
                    logoUrl: item.product.logoUrl,
                    featuredImageUrl: item.product.logoUrl,
                    thumbnailImages: [],
                    previewImages: [],
                    categories: [],
                    statusTags: undefined,
                    voteCount: item.product.voteCount,
                    viewCount: item.product.viewCount,
                    commentCount: item.product.commentCount,
                    founder: {
                        id: String(item.product.createdBy.id),
                        name: item.product.createdBy.name,
                        username: item.product.createdBy.username || '',
                    },
                    isVoted: false,
                    weekRank: item.position,
                }
            })

        return {
            success: true,
            data: featuredProducts,
        }
    } catch (error) {
        console.error('Failed to fetch featured products:', error)

        if (error instanceof HttpError) {
            return {
                success: false,
                error: {
                    message: error.message,
                    code: error.code,
                },
            }
        }

        return {
            success: false,
            error: {
                message: 'Не удалось загрузить избранные продукты',
                code: 'UNKNOWN_ERROR',
            },
        }
    }
}

// ============================================================================
// PRODUCTS LIST (Public)
// ============================================================================

/**
 * Get list of approved products with optional filters
 * Public endpoint - no authentication required
 * @deprecated Use listProductsGroupedByWeek for new grouped API
 */
export async function listProducts(params?: ListProductsParams): Promise<{
    success: boolean
    data?: ProductSummaryApi[]
    error?: ApiError
}> {
    try {
        const response = await httpClient.get<ProductListResponse>('/products', {
            params: {
                weekStart: params?.weekStart,
                categoryId: params?.categoryId,
                sort: params?.sort,
            }
        })
        return {success: true, data: response.products}
    } catch (error) {
        console.error('Failed to fetch products:', error)

        if (error instanceof HttpError) {
            return {
                success: false,
                error: {
                    message: error.message,
                    code: error.code,
                },
            }
        }

        return {
            success: false,
            error: {
                message: 'Не удалось загрузить продукты',
                code: 'UNKNOWN_ERROR',
            },
        }
    }
}

/**
 * Get list of approved products grouped by weeks
 * Public endpoint - supports infinite scroll pagination
 *
 * @param params.beforeWeekId - Fetch weeks older than this week ID (for pagination)
 * @param params.limit - Number of weeks to return
 * @param params.categoryId - Filter by category
 * @param params.sort - Sort order within weeks
 */
export async function listProductsGroupedByWeek(params?: ListGroupedProductsParams): Promise<{
    success: boolean
    data?: GroupedProductListResponse
    error?: ApiError
}> {
    try {
        const response = await httpClient.get<GroupedProductListResponse>('/products', {
            params: {
                beforeWeekId: params?.beforeWeekId,
                limit: params?.limit,
                categoryId: params?.categoryId,
                sort: params?.sort,
                weekStart: params?.weekStart,
            }
        })
        return {success: true, data: response}
    } catch (error) {
        console.error('Failed to fetch grouped products:', error)

        if (error instanceof HttpError) {
            return {
                success: false,
                error: {
                    message: error.message,
                    code: error.code,
                },
            }
        }

        return {
            success: false,
            error: {
                message: 'Не удалось загрузить продукты',
                code: 'UNKNOWN_ERROR',
            },
        }
    }
}

/**
 * placeholder logo URL
 */
export function getPlaceholderLogo(): string {
    return "/placeholder-logo.svg"
}

/**
 * Map ProductSummaryApi to the format expected by ProductCardPH
 */
export function mapProductSummaryToCardProduct(summary: ProductSummaryApi) {
    return {
        id: String(summary.id),
        slug: summary.slug,
        name: summary.name,
        description: summary.description,
        tagline: summary.tagline,
        logoUrl: summary.logoUrl || getPlaceholderLogo(),
        websiteUrl: summary.websiteUrl,
        screenshots: summary.previewImages.map(img => img.imageUrl),
        categories: summary.categories,
        statusTags: mapStatusTags(summary.statusTags),
        voteCount: summary.voteCount,
        viewCount: summary.viewCount,
        commentCount: summary.commentCount,
        isVoted: summary.isVoted,
        launchDate: summary.launchDate ? new Date(summary.launchDate) : undefined,
        founder: {
            id: String(summary.createdBy.id),
            name: summary.createdBy.name,
            username: summary.createdBy.username || '',
            avatarUrl: summary.createdBy.avatarUrl || undefined,
        },
    }
}

// ============================================================================
// UPCOMING LAUNCHES
// ============================================================================

export interface UpcomingLaunchesResponse {
    products: ProductSummaryApi[]
}

export async function getUpcomingLaunches() {
    const response = await httpClient.get<UpcomingLaunchesResponse>('/products/upcoming', { skipAuth: true })
    return response.products.map(mapProductSummaryToCardProduct)
}

// ============================================================================
// MY PRODUCTS (Authenticated)
// ============================================================================

/**
 * Get current user's products with full details
 * Requires authentication
 */
export async function getMyProducts(): Promise<{
    success: boolean
    data?: MyProductItem[]
    error?: ApiError
}> {
    try {
        const response = await httpClient.get<MyProductsResponse>('/products/my')

        return {
            success: true,
            data: response.products,
        }
    } catch (error) {
        console.error('Failed to fetch my products:', error)

        if (error instanceof HttpError) {
            return {
                success: false,
                error: {
                    message: error.message,
                    code: error.code,
                },
            }
        }

        return {
            success: false,
            error: {
                message: 'Не удалось загрузить ваши продукты',
                code: 'UNKNOWN_ERROR',
            },
        }
    }
}

// ============================================================================
// DELETE PRODUCT (Authenticated - Owner only)
// ============================================================================

/**
 * Delete a product permanently (owner only)
 * DELETE /api/v1/products/{slug} — returns 204 No Content
 */
export async function deleteProduct(slug: string): Promise<void> {
    try {
        await httpClient.delete(`/products/${slug}`)
    } catch (error) {
        console.error(`Failed to delete product ${slug}:`, error)
        throw error
    }
}

// ============================================================================
// RESUBMIT PRODUCT (Authenticated - Owner only)
// ============================================================================

export interface ResubmitProductResponse {
    success: boolean
    message: string
    slug: string
}

/**
 * Resubmit a rejected/hidden product back to moderation queue
 * POST /api/v1/products/{slug}/resubmit
 */
export async function resubmitProduct(slug: string): Promise<ResubmitProductResponse> {
    try {
        return await httpClient.post<ResubmitProductResponse>(`/products/${slug}/resubmit`, {})
    } catch (error) {
        console.error(`Failed to resubmit product ${slug}:`, error)
        throw error
    }
}

// ============================================================================
// VOTING (Authenticated)
// ============================================================================

/**
 * Toggle vote on a product (vote or unvote)
 */
export async function toggleProductVote(slug: string): Promise<{
    success: boolean
    data?: VoteResponse
    error?: VoteError
}> {
    try {
        const response = await httpClient.post<VoteResponse>(`/products/${slug}/vote`, {})

        return {
            success: true,
            data: response,
        }
    } catch (error) {
        console.error('Vote toggle failed:', error)

        if (error instanceof HttpError) {
            return {
                success: false,
                error: {
                    message: error.message,
                    code: error.code,
                },
            }
        }

        return {
            success: false,
            error: {
                message: 'Не удалось изменить голос',
                code: 'UNKNOWN_ERROR',
            },
        }
    }
}

// ============================================================================
// UNIFIED SEARCH TYPES
// ============================================================================

export interface ProductSearchItem {
    id: number
    slug: string
    name: string
    tagline: string
    logoUrl: string
    voteCount: number
    commentCount: number
}

export interface SearchResponse {
    products: ProductSearchItem[]
    users: UserSummary[]
}

// ============================================================================
// UNIFIED SEARCH (Public)
// ============================================================================

/**
 * Unified search - searches both products and users
 * GET /api/v1/search?q={query}
 */
export async function unifiedSearch(query: string): Promise<{
    success: boolean
    data?: SearchResponse
    error?: ApiError
}> {
    try {
        if (!query.trim()) {
            return { success: true, data: { products: [], users: [] } }
        }

        const response = await httpClient.get<SearchResponse>('/search', {
            params: { q: query.trim() },
            skipAuth: true,
        })

        return { success: true, data: response }
    } catch (error) {
        console.error('Search failed:', error)

        if (error instanceof HttpError) {
            return {
                success: false,
                error: {
                    message: error.message,
                    code: error.code,
                },
            }
        }

        return {
            success: false,
            error: {
                message: 'Не удалось выполнить поиск',
                code: 'UNKNOWN_ERROR',
            },
        }
    }
}

// ============================================================================
// Q&A TYPES
// ============================================================================

export interface QaItemResponse {
    id: number
    productId: number
    question: string
    answer: string | null
    askedBy: UserSummary | null
    upvotes: number
    createdAt: string
    answeredAt: string | null
}

export interface QaListResponse {
    items: QaItemResponse[]
    total: number
}

// ============================================================================
// Q&A API FUNCTIONS
// ============================================================================

/**
 * Get all Q&A items for a product (public)
 */
export async function getProductQA(slug: string): Promise<{
    success: boolean
    data?: QaListResponse
    error?: ApiError
}> {
    try {
        const response = await httpClient.get<QaListResponse>(`/products/${slug}/qa`)
        return {
            success: true,
            data: response,
        }
    } catch (error) {
        console.error('Failed to fetch Q&A:', error)

        if (error instanceof HttpError) {
            return {
                success: false,
                error: {
                    message: error.message,
                    code: error.code,
                },
            }
        }

        return {
            success: false,
            error: {
                message: 'Не удалось загрузить вопросы',
                code: 'UNKNOWN_ERROR',
            },
        }
    }
}

/**
 * Create a new question for a product (authenticated)
 */
export async function createQuestion(slug: string, question: string): Promise<{
    success: boolean
    data?: QaItemResponse
    error?: ApiError
}> {
    try {
        const response = await httpClient.post<QaItemResponse>(`/products/${slug}/qa`, {
            question,
        })
        return {
            success: true,
            data: response,
        }
    } catch (error) {
        console.error('Failed to create question:', error)

        if (error instanceof HttpError) {
            return {
                success: false,
                error: {
                    message: error.message,
                    code: error.code,
                },
            }
        }

        return {
            success: false,
            error: {
                message: 'Не удалось отправить вопрос',
                code: 'UNKNOWN_ERROR',
            },
        }
    }
}

/**
 * Answer a question (product owner only)
 */
export async function answerQuestion(qaId: number, answer: string): Promise<{
    success: boolean
    data?: QaItemResponse
    error?: ApiError
}> {
    try {
        const response = await httpClient.put<QaItemResponse>(`/qa/${qaId}/answer`, {
            answer,
        })
        return {
            success: true,
            data: response,
        }
    } catch (error) {
        console.error('Failed to answer question:', error)

        if (error instanceof HttpError) {
            return {
                success: false,
                error: {
                    message: error.message,
                    code: error.code,
                },
            }
        }

        return {
            success: false,
            error: {
                message: 'Не удалось ответить на вопрос',
                code: 'UNKNOWN_ERROR',
            },
        }
    }
}
